import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    TextField,
    IconButton,
    Chip,
    InputAdornment,
    CircularProgress,
    Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    collection,
    getDocs,
    query as fsQuery,
    orderBy,
    where,
    limit as fsLimit,
    startAfter,
    DocumentSnapshot,
    getCountFromServer,
    Timestamp
} from 'firebase/firestore';
import { firestoreInstance } from 'config/ApiService';

interface InactivityReminder {
    id: string;
    channel: string;
    email: string;
    customer_name: string;
    type: string;
    user_id: string;
    content: string;
    status: 'pending' | 'sent' | 'failed';
    created_at: Timestamp;
}

export const PhioengageInactivityReminderQueue: React.FC = () => {
    // Remote-driven state
    const [rows, setRows] = useState<InactivityReminder[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resendingIds, setResendingIds] = useState<Set<string>>(new Set());
    // Cursors: for page N, we store lastVisible of page N
    const [pageCursors, setPageCursors] = useState<Array<DocumentSnapshot | null>>([]);

    // Utility: tokenize query for array-contains-any on `search_index`
    const searchTokens = useMemo(() => {
        const q = (searchQuery || '').toLowerCase().trim();
        if (!q) return [] as string[];
        const tokens = q
            .split(/[^a-z0-9]+/i)
            .map((t) => t.trim())
            .filter((t) => t.length > 0);
        // Firestore array-contains-any supports up to 10 values
        return Array.from(new Set(tokens)).slice(0, 10);
    }, [searchQuery]);

    const buildBaseQuery = () => {
        const colRef = collection(firestoreInstance(), 'phioengage_inactivity_reminders');
        const constraints: any[] = [];
        // Order by a stable field; using created_at ensures deterministic pagination
        constraints.push(orderBy('created_at', 'desc'));
        if (searchQuery) {
            constraints.push(where('search_index', 'array-contains-any', [searchQuery]));
        }
        return fsQuery(colRef, ...constraints);
    };

    const fetchTotal = async () => {
        try {
            const q = buildBaseQuery();
            const agg = await getCountFromServer(q);
            setTotalCount(agg.data().count);
        } catch (e: any) {
            console.error('Failed to fetch total count', e);
            setError('Failed to load total count');
        }
    };

    const fetchPage = async (pageIndex: number) => {
        setLoading(true);
        setError(null);
        try {
            const base = buildBaseQuery();
            const startCursor = pageIndex > 0 ? (pageCursors[pageIndex - 1] ?? null) : null;
            const q = startCursor
                ? fsQuery(base, startAfter(startCursor), fsLimit(rowsPerPage))
                : fsQuery(base, fsLimit(rowsPerPage));
            const snap = await getDocs(q);
            const items: InactivityReminder[] = snap.docs.map((d) => {
                const data = d.data() as any;
                return {
                    id: d.id,
                    channel: data.channel,
                    email: data.email,
                    customer_name: data.customer_name,
                    type: data.type,
                    user_id: data.user_id,
                    status: data.status,
                    created_at: data.created_at
                } as InactivityReminder;
            });
            const lastVisible = snap.docs[snap.docs.length - 1] ?? null;
            setRows(items);
            setPageCursors((prev) => {
                const next = prev.slice();
                next[pageIndex] = lastVisible;
                return next;
            });
        } catch (e: any) {
            console.error('Failed to fetch page', e);
            setError('Failed to load reminders');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = async (_event: unknown, newPage: number) => {
        // Navigate using stored cursors; sequential fetching builds cursors progressively
        setPage(newPage);
        await fetchPage(newPage);
    };

    const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value, 10);
        setRowsPerPage(value);
        setPage(0);
        setPageCursors([]);
        await fetchTotal();
        await fetchPage(0);
    };

    const handleSearchChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
        setPage(0); // Reset to first page on search
        setPageCursors([]);
    };

    const handleResend = async (id: string) => {
        setResendingIds((prev) => new Set(prev).add(id));

        try {
            // TODO: Implement actual resend API call
            await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulated API call
            console.log(`Resending reminder with id: ${id}`);
            // Optionally refresh data or update status
        } catch (error) {
            console.error('Failed to resend reminder:', error);
        } finally {
            setResendingIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
            });
        }
    };

    const getStatusColor = (status: InactivityReminder['status']) => {
        switch (status) {
            case 'sent':
                return 'success';
            case 'failed':
                return 'error';
            case 'pending':
                return 'warning';
            default:
                return 'default';
        }
    };

    // Initial and reactive loads: search & rowsPerPage changes trigger count + first page
    useEffect(() => {
        (async () => {
            await fetchTotal();
            await fetchPage(0);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTokens, rowsPerPage]);

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                Inactivity Reminder Logs
            </Typography>

            <Box mb={3}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by email, customer name, type, user ID, or content..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }
                    }}
                />
            </Box>
            {error && (
                <Box mb={2}>
                    <Typography variant="body1" color="error">
                        {error}
                    </Typography>
                </Box>
            )}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <strong>Queued On</strong>
                            </TableCell>
                            <TableCell>
                                <strong>User ID</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Channel</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Email</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Customer Name</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Type</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Status</strong>
                            </TableCell>
                            <TableCell align="center">
                                <strong>Actions</strong>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        gap={1}
                                        py={2}
                                    >
                                        <CircularProgress size={20} />
                                        <Typography variant="body2">Loading...</Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Typography variant="body2" color="textSecondary">
                                        No reminders found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>
                                        {row.created_at?.toDate().toLocaleString()}
                                    </TableCell>
                                    <TableCell>{row.user_id}</TableCell>
                                    <TableCell>{row.channel}</TableCell>
                                    <TableCell>{row.email}</TableCell>
                                    <TableCell>{row.customer_name}</TableCell>
                                    <TableCell>{row.type}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.status}
                                            color={getStatusColor(row.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {row.status === 'failed' && (
                                            <Tooltip title="Resend reminder">
                                                <IconButton
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => handleResend(row.id)}
                                                    disabled={resendingIds.has(row.id)}
                                                >
                                                    {resendingIds.has(row.id) ? (
                                                        <CircularProgress size={20} />
                                                    ) : (
                                                        <RefreshIcon />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        </Box>
    );
};

export default PhioengageInactivityReminderQueue;
